import { ModelMessage } from '@/types/msq'
import { CompressedPromptConfig } from '@/types/compressed'
import { compressData, decompressData } from '@/utils/compression'
import { Commit } from 'vuex'

export interface PromptInterface {
  id: string
  type: 'model' | 'agent'
}

export interface PromptInterfaceTarget extends PromptInterface {
  timestamp: number
}

export interface PromptHistory {
  context: ModelMessage[]
  response: string
}

export interface PromptConfig {
  id: string
  name: string
  timestamp: number
  promptList?: ModelMessage[]
  context: ModelMessage[]
  target?: PromptInterfaceTarget
  temperature: number
  maxTokens: number
  history?: PromptHistory[]
}

export interface PromptConfigs {
  [key: string]: PromptConfig
}

export interface PromptStudioState {
  configs: Record<string, CompressedPromptConfig> // Changed to use compressed type
}

export const prompts = () => ({
  state: <PromptStudioState>{
    configs: {}
  },
  mutations: {
    setPromptConfig(state: PromptStudioState, { id, config }: { id: string; config: PromptConfig }) {
      // Compress context
      const compressedConfig: CompressedPromptConfig = {
        ...config,
        context: compressData(config.context)
      }
      state.configs[id] = compressedConfig
    },
    deletePrompt(state: PromptStudioState, id: string) {
      delete state.configs[id]
    },
    deleteAllPrompts(state: PromptStudioState) {
      state.configs = {}
    }
  },
  actions: {
    setPromptConfig({ commit }: { commit: Commit }, config: PromptConfig) {
      // The config passed in now contains the full context, including toolEvents.
      // The mutation will handle compression.
      commit('setPromptConfig', { id: config.id, config })
    },
    deletePrompt({ commit }: { commit: Commit }, id: string) {
      commit('deletePrompt', id)
    },
    deleteAllPrompts({ commit }: { commit: Commit }) {
      commit('deleteAllPrompts')
    }
  },
  getters: {
    promptList: (state: PromptStudioState): PromptConfig[] => {
      return (
        Object.keys(state.configs)
          .map((key) => {
            const config = state.configs[key]
            let decompressedConfig: PromptConfig = {
              ...config,
              context: decompressData(config.context)
            }

            // In-memory migration for legacy prompt structures
            if (
              (!decompressedConfig.context || decompressedConfig.context.length === 0) &&
              (decompressedConfig.promptList || decompressedConfig.history)
            ) {
              const newContext = (decompressedConfig.promptList || []).concat(
                (decompressedConfig.history || []).map((h) => ({ role: 'assistant', content: h.response }))
              )
              decompressedConfig.context = newContext
              delete decompressedConfig.promptList
              delete decompressedConfig.history
            }

            return decompressedConfig
          })
          .sort((a, b) => (b.target != null ? b.target.timestamp : b.timestamp) - a.timestamp) || []
      )
    },

    // Add a getter for individual prompt config
    promptConfigById: (state: PromptStudioState) => (id: string): PromptConfig | undefined => {
      const config = state.configs[id]
      if (!config) return undefined

      // Return decompressed and migrated config
      let decompressedConfig: PromptConfig = {
        ...config,
        context: decompressData(config.context)
      }

      // In-memory migration for legacy prompt structures
      if (
        (!decompressedConfig.context || decompressedConfig.context.length === 0) &&
        (decompressedConfig.promptList || decompressedConfig.history)
      ) {
        const newContext = (decompressedConfig.promptList || []).concat(
          (decompressedConfig.history || []).map((h) => ({ role: 'assistant', content: h.response }))
        )
        decompressedConfig.context = newContext
        delete decompressedConfig.promptList
        delete decompressedConfig.history
      }

      return decompressedConfig
    }
  }
})
